/**
 * Classe de base pour toutes les modales - VERSION CORRIGÉE
 * Fournit les fonctionnalités communes : ouverture, fermeture, navigation clavier, etc.
 */

export interface ModalConfig {
  id: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  className?: string;
  zIndex?: 10 | 20 | 30 | 40 | 50;
}

export interface ModalElements {
  modal?: HTMLElement;
  backdrop?: HTMLElement;
  content?: HTMLElement;
  closeBtn?: HTMLElement;
  header?: HTMLElement;
  body?: HTMLElement;
  footer?: HTMLElement;
}

export abstract class BaseModal {
  public config: ModalConfig;
  protected elements: ModalElements = {};
  protected cleanup: Array<() => void> = [];
  protected isOpen: boolean = false;

  constructor(config: ModalConfig) {
    this.config = {
      closeOnBackdrop: true,
      closeOnEscape: true,
      showCloseButton: true,
      maxWidth: '5xl',
      zIndex: 50,
      ...config
    };
  }

  /**
   * Template method - doit être implémentée par les classes filles
   */
  protected abstract renderContent(): string;

  /**
   * Lifecycle hook - appelée après l'ouverture de la modale
   */
  protected onOpen(): void {}

  /**
   * Lifecycle hook - appelée avant la fermeture de la modale
   */
  protected onClose(): void {}

  /**
   * Lifecycle hook - appelée après le rendu
   */
  protected onRender(): void {}

  /**
   * Retourne les classes CSS pour maxWidth
   */
  private getMaxWidthClass(): string {
    const maxWidthMap = {
      'sm': 'max-w-sm',
      'md': 'max-w-md', 
      'lg': 'max-w-lg',
      'xl': 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl'
    };
    return maxWidthMap[this.config.maxWidth || '5xl'];
  }

  /**
   * Retourne les classes CSS pour zIndex
   */
  private getZIndexClass(): string {
    const zIndexMap = {
      10: 'z-10',
      20: 'z-20',
      30: 'z-30',
      40: 'z-40',
      50: 'z-50'
    };
    return zIndexMap[this.config.zIndex || 50];
  }

  /**
   * Crée la structure HTML de base de la modale
   */
  protected createModalStructure(): string {
    const maxWidthClass = this.getMaxWidthClass();
    const zIndexClass = this.getZIndexClass();

    return `
      <div 
        id="${this.config.id}" 
        class="fixed inset-0 ${zIndexClass} flex items-center justify-center opacity-0 pointer-events-none transition-all duration-500 ${this.config.className || ''}" 
        role="dialog" 
        aria-modal="true"
      >
        <!-- Backdrop avec effet flou -->
        <div class="modal-backdrop absolute inset-0 bg-[#131313]/75 backdrop-blur-xl opacity-0 transition-opacity duration-500"></div>

        <!-- Contenu de la modale -->
        <div class="modal-content relative ${maxWidthClass} w-full h-auto max-h-[90vh] bg-gradient-to-br from-[#34495e]/95 to-[#2c3e50]/95 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl transform translate-y-8 scale-95 opacity-0 transition-all duration-500 p-6 md:p-8 mx-4 flex flex-col">
          
          <!-- Éléments décoratifs lumineux -->
          <div class="absolute -top-20 -right-20 w-40 h-40 bg-[#ed4c67] rounded-full filter blur-[80px] opacity-30 z-0"></div>
          <div class="absolute -bottom-20 -left-20 w-40 h-40 bg-[#3498db] rounded-full filter blur-[80px] opacity-30 z-0"></div>

          ${this.config.showCloseButton ? `
          <!-- Bouton de fermeture en position absolue -->
          <button 
            class="modal-close-btn absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all duration-300 z-30 border border-white/10 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/30" 
            aria-label="Fermer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          ` : ''}

          <!-- Navigation intelligemment positionnée sous le bouton fermer -->
          <div class="modal-header flex items-center justify-center pt-4 pb-6 relative z-20">
            <div class="flex items-center gap-4">
              <button id="prev-service-btn" class="nav-btn flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed" disabled>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>

              <div id="service-position" class="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/70 text-sm font-medium border border-white/10">
                <span id="current-service-index">1</span>
                <span class="w-1 h-1 bg-white/40 rounded-full"></span>
                <span id="total-services">7</span>
              </div>

              <button id="next-service-btn" class="nav-btn flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Contenu dynamique -->
          <div class="modal-body relative z-10 flex-1 overflow-y-auto">
            ${this.renderContent()}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Initialise la modale dans le DOM
   */
  public init(): void {
    // Vérifier si la modale existe déjà
    const existing = document.getElementById(this.config.id);
    if (existing) {
      console.warn(`Modal ${this.config.id} existe déjà, suppression...`);
      existing.remove();
    }

    // Créer le conteneur et l'injecter
    const container = document.createElement('div');
    container.innerHTML = this.createModalStructure();
    const modalElement = container.firstElementChild as HTMLElement;
    
    if (!modalElement) {
      console.error('Erreur lors de la création de l\'élément modal');
      return;
    }

    document.body.appendChild(modalElement);

    // Référencer les éléments
    this.elements.modal = modalElement;
    this.elements.backdrop = modalElement.querySelector('.modal-backdrop') as HTMLElement;
    this.elements.content = modalElement.querySelector('.modal-content') as HTMLElement;
    this.elements.body = modalElement.querySelector('.modal-body') as HTMLElement;
    
    if (this.config.showCloseButton) {
      this.elements.closeBtn = modalElement.querySelector('.modal-close-btn') as HTMLElement;
    }

    // Vérifier que les éléments essentiels sont présents
    if (!this.elements.modal || !this.elements.backdrop || !this.elements.content || !this.elements.body) {
      console.error('Éléments essentiels de la modale non trouvés');
      return;
    }

    this.setupEventListeners();
    this.onRender();
    
    console.log(`✅ Modal ${this.config.id} initialisée avec succès`);
  }

  /**
   * Configure les écouteurs d'événements
   */
  protected setupEventListeners(): void {
    if (!this.elements.modal) return;

    // Fermeture sur clic du backdrop
    if (this.config.closeOnBackdrop && this.elements.backdrop) {
      const backdropHandler = (e: Event) => {
        if (e.target === this.elements.backdrop) {
          this.close();
        }
      };
      this.elements.backdrop.addEventListener('click', backdropHandler);
      this.cleanup.push(() => {
        this.elements.backdrop?.removeEventListener('click', backdropHandler);
      });
    }

    // Fermeture sur bouton close
    if (this.elements.closeBtn) {
      const closeHandler = () => this.close();
      this.elements.closeBtn.addEventListener('click', closeHandler);
      this.cleanup.push(() => {
        this.elements.closeBtn?.removeEventListener('click', closeHandler);
      });
    }

    // Fermeture sur touche Escape
    if (this.config.closeOnEscape) {
      const escapeHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      };
      document.addEventListener('keydown', escapeHandler);
      this.cleanup.push(() => {
        document.removeEventListener('keydown', escapeHandler);
      });
    }
  }

  /**
   * Ouvre la modale
   */
  public open(): void {
    if (!this.elements.modal || this.isOpen) {
      console.warn(`Impossible d'ouvrir la modal ${this.config.id}`);
      return;
    }

    this.isOpen = true;
    this.onOpen();

    // Afficher la modale
    this.elements.modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Appliquer les styles d'ouverture
    this.elements.modal.style.opacity = '1';
    this.elements.modal.style.pointerEvents = 'auto';
    this.elements.modal.style.visibility = 'visible';

    if (this.elements.content) {
      this.elements.content.style.opacity = '1';
      this.elements.content.style.transform = 'translateY(0) scale(1)';
    }

    if (this.elements.backdrop) {
      this.elements.backdrop.style.opacity = '1';
    }

    console.log(`🎭 Modal ${this.config.id} ouverte`);
  }

  /**
   * Ferme la modale
   */
  public close(): void {
    if (!this.elements.modal || !this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.onClose();

    // Animation de fermeture
    if (this.elements.content) {
      this.elements.content.style.opacity = '0';
      this.elements.content.style.transform = 'translateY(8px) scale(0.95)';
    }

    if (this.elements.backdrop) {
      setTimeout(() => {
        if (this.elements.backdrop) {
          this.elements.backdrop.style.opacity = '0';
        }
      }, 100);
    }

    // Finaliser la fermeture après l'animation
    setTimeout(() => {
      if (this.elements.modal) {
        this.elements.modal.classList.remove('active');
        this.elements.modal.style.opacity = '';
        this.elements.modal.style.pointerEvents = '';
        this.elements.modal.style.visibility = '';
        document.body.style.overflow = '';
      }
    }, 300);

    console.log(`🎭 Modal ${this.config.id} fermée`);
  }

  /**
   * Met à jour le contenu de la modale
   */
  public updateContent(): void {
    if (this.elements.body) {
      this.elements.body.innerHTML = this.renderContent();
      this.onRender();
    }
  }

  /**
   * Nettoie les ressources
   */
  public destroy(): void {
    this.cleanup.forEach(fn => fn());
    this.cleanup = [];
    
    if (this.elements.modal) {
      this.elements.modal.remove();
    }
    
    this.elements = {};
    this.isOpen = false;
    
    console.log(`🗑️ Modal ${this.config.id} détruite`);
  }

  /**
   * Getters utiles
   */
  public get isModalOpen(): boolean {
    return this.isOpen;
  }

  public get modalElement(): HTMLElement | undefined {
    return this.elements.modal;
  }
}
